import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

const LINKS = [
  { label: 'Collections', to: '/collections', id: 'collections' },
  { label: 'Atelier', to: '/', scroll: '#configurator', id: 'configurator' },
  { label: 'Craft', to: '/craft', id: 'craft' },
  { label: 'Partners', to: '/partners', id: 'partners' },
  { label: 'Contact', to: '/contact', id: 'contact' },
];

export const scrollToSection = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -70, duration: 1.4 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  }
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (e, link) => {
    e.preventDefault();
    setOpen(false);
    if (link.scroll) {
      if (pathname === '/') {
        scrollToSection(link.scroll);
      } else {
        navigate('/', { state: { scrollTo: link.scroll } });
      }
    } else {
      navigate(link.to);
    }
  };

  const isActive = (link) => !link.scroll && pathname === link.to;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || pathname !== '/' ? 'backdrop-blur-xl bg-[#0A0A0B]/85 border-b border-[#D4AF37]/20' : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[70px] flex items-center justify-between">
        <a
          href="/"
          data-testid="nav-logo-link"
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            navigate('/');
          }}
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/img/brand_logo.png"
            alt="Jakson Faucets & Accessories"
            className="h-9 w-auto"
          />
          <span className="hidden sm:block font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400 group-hover:text-[#D4AF37] transition-colors">
            Redefining Water
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.to}
              data-testid={`nav-link-${l.id}`}
              onClick={(e) => go(e, l)}
              className={`font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                isActive(l) ? 'text-[#D4AF37]' : 'text-zinc-400 hover:text-[#F5E6AD]'
              }`}
            >
              {l.label}
            </a>
          ))}
          <button
            data-testid="nav-pricelist-btn"
            onClick={() => navigate('/contact')}
            className="font-mono text-[11px] uppercase tracking-[0.25em] border border-[#D4AF37]/50 text-[#D4AF37] px-5 py-2.5 hover:bg-[#D4AF37] hover:text-[#0A0A0B] transition-colors duration-300"
          >
            Get Price List
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-zinc-300 hover:text-[#D4AF37] transition-colors"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass-dark overflow-hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.to}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={(e) => go(e, l)}
                  className={`font-mono text-xs uppercase tracking-[0.25em] transition-colors ${
                    isActive(l) ? 'text-[#D4AF37]' : 'text-zinc-300 hover:text-[#D4AF37]'
                  }`}
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
